import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import PhotoUpload from './PhotoUpload';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfessionalProfileSetup = () => {
  const navigate = useNavigate();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Basic info (from registration - pre-populated)
    name: '',
    email: '',
    age: '',
    bio: '',
    
    // Partner Preferences (3 questions)
    partner_age_range_min: 25,
    partner_age_range_max: 35,
    appearance_importance: 3,
    
    // Relationship Goals (4 questions)
    relationship_goals: '',
    commitment_timeline: '',
    family_plans: '',
    deal_breakers: [],
    
    // Lifestyle Compatibility (4 questions)
    ideal_date_type: '',
    social_preference: '',
    activity_level: 3,
    communication_style: '',
    
    // Values & Compatibility (4 questions)
    education_importance: 3,
    career_importance: 3,
    lifestyle_values: [],
    relationship_pace: ''
  });
  
  const [isAnimating, setIsAnimating] = useState(false);
  const [completionScore, setCompletionScore] = useState(20); // Starts at 20% with basic info
  const [userLoaded, setUserLoaded] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photosUploaded, setPhotosUploaded] = useState(false);

  // Pre-populate form with user data from registration
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axios.get(`${API}/me`);
        const userData = response.data;
        
        setFormData(prevData => ({
          ...prevData,
          name: userData.name || '',
          email: userData.email || '',
          age: userData.age || '',
          bio: userData.bio || '',
          interests: Array.isArray(userData.interests) ? userData.interests.join(', ') : (userData.interests || '')
        }));
        
        setUserLoaded(true);
        console.log('✅ Pre-populated profile data from registration');
      } catch (error) {
        console.error('Error fetching user data:', error);
        setUserLoaded(true); // Continue even if fetch fails
      }
    };

    fetchUserData();
  }, []);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [currentStep]);

  // Streamlined matching questions - focusing on partner preferences, not user traits
  // (User traits like age, gender, location, bio, interests already captured in registration)
  const partnerPreferenceQuestions = [
    // Physical Partner Preferences (3 questions)
    {
      id: 'partner_age_range_min',
      question: 'Minimum age you\'d like to date',
      type: 'number',
      range: [18, 65],
      category: 'Partner Preferences'
    },
    {
      id: 'partner_age_range_max', 
      question: 'Maximum age you\'d like to date',
      type: 'number',
      range: [18, 65],
      category: 'Partner Preferences'
    },
    {
      id: 'appearance_importance',
      question: 'How important is physical attraction',
      type: 'scale',
      range: [1, 5],
      labels: ['Not important', 'Very important'],
      category: 'Partner Preferences'
    },
    
    // Relationship Goals (4 questions)
    {
      id: 'relationship_goals',
      question: 'What are you looking for',
      type: 'select',
      options: ['Casual dating', 'Serious relationship', 'Marriage potential', 'Companionship', 'Still exploring'],
      category: 'Relationship Goals'
    },
    {
      id: 'commitment_timeline',
      question: 'Ideal timeline for commitment',
      type: 'select',
      options: ['Take it slow', 'Within 6 months', 'Within a year', '1-2 years', 'No rush'],
      category: 'Relationship Goals'
    },
    {
      id: 'family_plans',
      question: 'Do you want children',
      type: 'select',
      options: ['Yes, definitely', 'Maybe someday', 'Not sure', 'No, never', 'Already have children'],
      category: 'Relationship Goals'
    },
    {
      id: 'deal_breakers',
      question: 'Important deal breakers',
      type: 'multi-select',
      options: ['Smoking', 'Heavy drinking', 'No career ambition', 'Different religion', 'Different politics', 'No exercise'],
      category: 'Relationship Goals'
    },
    
    // Lifestyle Compatibility (4 questions)
    {
      id: 'ideal_date_type',
      question: 'Your ideal type of date',
      type: 'select',
      options: ['Quiet dinner', 'Adventure activity', 'Cultural event', 'Casual hangout', 'Romantic setting'],
      category: 'Lifestyle Compatibility'
    },
    {
      id: 'social_preference',
      question: 'Preferred social style',
      type: 'select',
      options: ['Quiet nights in', 'Occasional outings', 'Regular socializing', 'Always out and about', 'Balanced mix'],
      category: 'Lifestyle Compatibility'
    },
    {
      id: 'activity_level',
      question: 'Preferred activity level',
      type: 'scale',
      range: [1, 5],
      labels: ['Prefer relaxation', 'Always active'],
      category: 'Lifestyle Compatibility'
    },
    {
      id: 'communication_style',
      question: 'Preferred communication style',
      type: 'select',
      options: ['Direct and honest', 'Gentle and supportive', 'Playful and fun', 'Deep and meaningful', 'Balanced approach'],
      category: 'Lifestyle Compatibility'
    },
    
    // Values & Compatibility (4 questions)
    {
      id: 'education_importance',
      question: 'How important is education level',
      type: 'scale',
      range: [1, 5],
      labels: ['Not important', 'Very important'],
      category: 'Values & Compatibility'
    },
    {
      id: 'career_importance',
      question: 'How important is career success',
      type: 'scale',
      range: [1, 5],
      labels: ['Not important', 'Very important'],
      category: 'Values & Compatibility'
    },
    {
      id: 'lifestyle_values',
      question: 'Important shared values',
      type: 'multi-select',
      options: ['Health & fitness', 'Travel', 'Career growth', 'Family time', 'Personal development', 'Financial stability'],
      category: 'Values & Compatibility'
    },
    {
      id: 'relationship_pace',
      question: 'Preferred relationship pace',
      type: 'select',
      options: ['Take things very slow', 'Moderate pace', 'Ready to commit quickly', 'Go with the flow', 'No preference'],
      category: 'Values & Compatibility'
    }
  ];

  const allQuestions = partnerPreferenceQuestions;

  // Calculate completion score based on answered questions
  useEffect(() => {
    const answeredQuestions = allQuestions.filter(q => {
      const value = formData[q.id];
      return value && (typeof value === 'string' ? value.trim() : value.length > 0 || value !== 0);
    }).length;
    
    const basicFields = ['name', 'email', 'age', 'bio'].filter(field => formData[field]).length;
    const photoScore = photos.length > 0 ? 20 : 0; // 20% for having photos
    const basicScore = (basicFields / 4) * 30; // 30% for basic fields
    const questionsScore = (answeredQuestions / allQuestions.length) * 50; // 50% for questions
    
    const totalScore = basicScore + photoScore + questionsScore;
    setCompletionScore(Math.round(totalScore));
  }, [formData, photos]);

  const handleQuestionChange = (questionId, value) => {
    setFormData(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🎯 Profile submission started');
    setIsAnimating(true);

    try {
      // First, save basic profile info to /api/me (name, age, bio from Step 1)
      const basicProfileData = {
        name: formData.name,
        age: formData.age ? parseInt(formData.age) : null,
        bio: formData.bio
      };
      
      console.log('📤 Saving basic profile data to /api/me:', basicProfileData);
      await axios.put(`${API}/me`, basicProfileData);
      console.log('✅ Basic profile saved to /api/me');
      
      // Then save detailed matching preferences to /api/detailed-profile
      // Include bio to keep it in sync with profile view page
      const profileData = {
        // Include bio so it syncs with profile view page
        bio: formData.bio,
        
        // Streamlined data structure focusing on partner preferences and relationship goals
        relationship_goals: formData.relationship_goals ? [formData.relationship_goals] : [],
        seriousness_level: formData.commitment_timeline,
        family_plans: formData.family_plans,
        timeline_expectations: formData.commitment_timeline,
        
        social_style: formData.social_preference,
        activity_level: formData.activity_level ? `Level ${formData.activity_level}` : null,
        
        education_importance: formData.education_importance ? `Level ${formData.education_importance}` : null,
        career_importance: formData.career_importance ? `Level ${formData.career_importance}` : null,
        
        communication_style: formData.communication_style,
        important_values: formData.lifestyle_values || [],
        dealbreakers: formData.deal_breakers || [],
        
        // Partner preferences for matching algorithm
        partner_age_range: {
          min: formData.partner_age_range_min || 18,
          max: formData.partner_age_range_max || 65
        },
        appearance_importance: formData.appearance_importance || 3,
        ideal_date_type: formData.ideal_date_type,
        relationship_pace: formData.relationship_pace
      };
      
      console.log('📤 Submitting detailed profile data:', profileData);
      const response = await axios.put(`${API}/detailed-profile`, profileData);
      console.log('✅ Detailed profile submitted successfully:', response.data);
      
      setTimeout(() => {
        console.log('🚀 Navigating to profile view');
        navigate('/profile/view');
      }, 1500);
    } catch (error) {
      console.error('❌ Error creating profile:', error);
      console.error('❌ Error details:', error.response?.data);
      setIsAnimating(false);
      
      // Show error to user
      alert(`Profile submission failed: ${error.response?.data?.detail || error.message}`);
    }
  };

  const renderQuestion = (question) => {
    const value = formData[question.id];
    
    switch (question.type) {
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleQuestionChange(question.id, e.target.value)}
            className="w-full bg-white/5 backdrop-blur-md border border-white/10 text-white rounded-xl py-4 px-4 focus:border-rose-400 focus:ring-2 focus:ring-rose-400/50 transition-all duration-300"
          >
            <option value="" className="bg-gray-800 text-gray-300">Select an option (optional)</option>
            {question.options.map(option => (
              <option key={option} value={option} className="bg-gray-800">{option}</option>
            ))}
          </select>
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleQuestionChange(question.id, e.target.value)}
            min={question.range[0]}
            max={question.range[1]}
            className="w-full bg-white/5 backdrop-blur-md border border-white/10 text-white rounded-xl py-4 px-4 focus:border-rose-400 focus:ring-2 focus:ring-rose-400/50 transition-all duration-300"
            placeholder="Enter age (optional)"
          />
        );
        
      case 'multi-select':
        return (
          <div className="grid grid-cols-2 gap-3">
            {question.options.map(option => (
              <label key={option} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(value || []).includes(option)}
                  onChange={(e) => {
                    const currentValues = value || [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter(v => v !== option);
                    handleQuestionChange(question.id, newValues);
                  }}
                  className="w-5 h-5 text-rose-500 bg-white/10 border-white/20 rounded focus:ring-rose-400"
                />
                <span className="text-white text-sm">{option}</span>
              </label>
            ))}
          </div>
        );
        
      case 'scale':
        return (
          <div className="space-y-4">
            <div className="flex justify-between text-sm text-gray-300">
              <span>{question.labels[0]}</span>
              <span>{question.labels[1]}</span>
            </div>
            <input
              type="range"
              min={question.range[0]}
              max={question.range[1]}
              value={value || 3}
              onChange={(e) => handleQuestionChange(question.id, parseInt(e.target.value))}
              className="w-full h-3 bg-white/10 rounded-lg appearance-none cursor-pointer slider-rose"
            />
            <div className="text-center">
              <span className="text-white font-medium">Level: {value || 3}</span>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  const questionsPerStep = 5;
  const totalSteps = Math.ceil(allQuestions.length / questionsPerStep) + 2; // +1 for basic info, +1 for photos
  const currentQuestions = currentStep <= 2 ? [] : allQuestions.slice((currentStep - 3) * questionsPerStep, (currentStep - 2) * questionsPerStep);

  if (isAnimating) {
    return (
      <div className="min-h-screen relative overflow-x-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-900 via-purple-900 to-pink-900"></div>
        
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <Card className="w-full max-w-md bg-black/20 backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl">
            <CardContent className="text-center py-12">
              <div className="w-16 h-16 border-4 border-rose-400 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-white mb-4">Creating Your Profile</h2>
              <p className="text-gray-300">Preparing your personalized matching experience</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Sophisticated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-rose-900/50 to-pink-900/80"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-rose-400/20 via-transparent to-transparent"></div>
      
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="max-w-2xl w-full">
          <Card className="bg-black/20 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-3xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-rose-500/20 via-pink-500/20 to-rose-600/20 backdrop-blur-md p-8 text-center border-b border-white/10">
              <h1 className="text-4xl font-light text-white mb-3">
                Profile
              </h1>
              <p className="text-gray-300 text-lg">
                Step {currentStep} of {totalSteps}
              </p>
              
              {/* Elegant Progress Bar */}
              <div className="relative bg-white/10 rounded-full h-2 overflow-hidden mt-6">
                <div 
                  className="absolute top-0 left-0 h-full bg-gradient-to-r from-rose-400 to-pink-400 rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${(currentStep / totalSteps) * 100}%` }}
                ></div>
              </div>
              
              {/* Completion Score */}
              <div className="mt-4">
                <div className="text-sm text-gray-300 mb-2">Profile Completion</div>
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-24 bg-white/10 rounded-full h-2">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 to-emerald-400 rounded-full transition-all duration-500"
                      style={{ width: `${completionScore}%` }}
                    ></div>
                  </div>
                  <span className="text-white font-medium">{completionScore}%</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  The more you share, the better your matches
                </p>
              </div>
            </div>

            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Step 1: Basic Profile */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-light text-white mb-3">Basic Information</h2>
                      <p className="text-gray-300">Information from your registration</p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-3">
                          Your Name
                          <span className="text-blue-400 text-xs ml-2">(from registration)</span>
                        </label>
                        <Input
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="bg-white/10 backdrop-blur-md border-white/20 text-white rounded-xl py-4"
                          placeholder={userLoaded ? "Loading..." : "Your full name"}
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-200 mb-3">
                          Age
                          <span className="text-blue-400 text-xs ml-2">(from registration)</span>
                        </label>
                        <Input
                          type="number"
                          value={formData.age}
                          onChange={(e) => setFormData({...formData, age: e.target.value})}
                          className="bg-white/10 backdrop-blur-md border-white/20 text-white rounded-xl py-4"
                          min="18"
                          max="100"
                          placeholder={userLoaded ? "Loading..." : "Your age"}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-3">
                        Email Address
                        <span className="text-green-400 text-xs ml-2">(from registration)</span>
                      </label>
                      <Input
                        type="email"
                        value={formData.email}
                        className="bg-white/5 backdrop-blur-md border-white/10 text-gray-300 rounded-xl py-4 cursor-not-allowed"
                        readOnly
                        title="Email from registration - contact support to change"
                        placeholder={userLoaded ? "Loading..." : "your@email.com"}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-200 mb-3">
                        About You
                        <span className="text-blue-400 text-xs ml-2">(can be updated)</span>
                      </label>
                      <Textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({...formData, bio: e.target.value})}
                        className="bg-white/10 backdrop-blur-md border-white/20 text-white rounded-xl resize-none"
                        placeholder={userLoaded ? "Loading..." : "Tell others what makes you unique..."}
                        rows={4}
                      />
                    </div>
                  </div>
                )}

                {/* Step 2: Photo Upload */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-light text-white mb-3">📸 Add Your Photos</h2>
                      <p className="text-gray-300 mb-3">Make a great first impression!</p>
                      
                      {/* Stats badges */}
                      <div className="flex flex-wrap gap-3 justify-center mb-4">
                        <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30 rounded-full px-4 py-2">
                          <span className="text-green-400 font-semibold text-sm">📊 10x More Matches</span>
                        </div>
                        <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-400/30 rounded-full px-4 py-2">
                          <span className="text-blue-400 font-semibold text-sm">⚡ 5x Faster Connections</span>
                        </div>
                        <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30 rounded-full px-4 py-2">
                          <span className="text-purple-400 font-semibold text-sm">💬 3x More Messages</span>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-400">
                        You can add photos now or skip and add them later in your profile
                      </p>
                    </div>
                    
                    {/* Warning if no photos */}
                    {!photosUploaded && (
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">⚠️</span>
                          <div className="flex-1">
                            <h3 className="text-yellow-400 font-semibold mb-1">No Photos Yet</h3>
                            <p className="text-yellow-200/80 text-sm">
                              Profiles without photos get significantly fewer matches. Upload at least one photo to maximize your success!
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Success message if photos uploaded */}
                    {photosUploaded && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">✅</span>
                          <div className="flex-1">
                            <h3 className="text-green-400 font-semibold mb-1">Great Job!</h3>
                            <p className="text-green-200/80 text-sm">
                              You've added {photos.length} photo{photos.length > 1 ? 's' : ''}. Your profile is looking much more attractive!
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
                      <PhotoUpload 
                        onPhotosChange={(newPhotos) => {
                          setPhotos(newPhotos);
                          setPhotosUploaded(newPhotos.length > 0);
                        }}
                        existingPhotos={photos}
                      />
                    </div>
                    
                    <div className="text-center space-y-2">
                      <p className="text-xs text-gray-400">
                        📁 Upload up to 6 photos • 🛡️ All photos are reviewed for safety
                      </p>
                      <p className="text-xs text-gray-500">
                        Tip: Photos with your face clearly visible work best!
                      </p>
                    </div>
                  </div>
                )}

                {/* Advanced Matching Questions */}
                {currentStep > 2 && (
                  <div className="space-y-6">
                    <div className="text-center mb-8">
                      <h2 className="text-2xl font-light text-white mb-3">
                        {currentQuestions[0]?.category}
                      </h2>
                      <p className="text-gray-300 text-sm">
                        These questions are optional, but help us find your perfect match
                      </p>
                    </div>
                    
                    <div className="space-y-8">
                      {currentQuestions.map((question, index) => (
                        <div key={question.id} className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
                          <label className="block text-sm font-medium text-gray-200 mb-4">
                            {question.question}
                            <span className="text-gray-400 text-xs ml-2">(optional)</span>
                          </label>
                          {renderQuestion(question)}
                        </div>
                      ))}
                    </div>
                    
                    <div className="text-center">
                      <p className="text-xs text-gray-400">
                        Skip any questions you're not comfortable answering
                      </p>
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex justify-between items-center pt-6 border-t border-white/10">
                  {currentStep > 1 ? (
                    <Button
                      type="button"
                      onClick={() => {
                        setCurrentStep(currentStep - 1);
                        // Scroll to top when moving to previous step
                        setTimeout(() => {
                          window.scrollTo({
                            top: 0,
                            left: 0,
                            behavior: 'smooth'
                          });
                        }, 100);
                      }}
                      className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
                    >
                      Previous
                    </Button>
                  ) : (
                    <div></div>
                  )}
                  
                  {currentStep < totalSteps ? (
                    <Button
                      type="button"
                      onClick={() => {
                        setCurrentStep(currentStep + 1);
                        // Scroll to top when moving to next step
                        setTimeout(() => {
                          window.scrollTo({
                            top: 0,
                            left: 0,
                            behavior: 'smooth'
                          });
                        }, 100);
                      }}
                      className="bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white px-8"
                    >
                      Continue
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={isAnimating}
                      className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white px-8"
                    >
                      Complete Profile
                    </Button>
                  )}
                </div>
                
                <div className="text-center">
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    className="text-gray-400 hover:text-white text-sm underline bg-transparent border-none"
                  >
                    Skip remaining questions and start dating
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalProfileSetup;